/**
 * Infinite Scroll — Dawn collection pages
 * --------------------------------------------------------------------------
 * Progressively loads the next paginated page of products and appends it to
 * the existing grid, with no full page reload.
 *
 * How it works
 *  - Determines the current page and the last page from the numbered
 *    pagination links (1, 2, 3 ... N). This is independent of the pagination
 *    "next/prev" arrow classes, which are swapped in this theme's customised
 *    pagination.liquid, so relying on them is not safe.
 *  - Records the first page's product count as the "page size". A loaded page
 *    that returns fewer items than the page size is treated as the last page,
 *    so we stop exactly there and never request an empty page.
 *  - Builds each request from the current URL (which already carries any active
 *    filter + sort params) and fetches it through the Shopify Section Rendering
 *    API (?section_id=...), returning only the product-grid section.
 *  - A sentinel after the grid (in #ProductGridContainer's block flow, not
 *    inside the CSS grid) is watched by an IntersectionObserver, with a
 *    throttled scroll handler as a fallback trigger.
 *  - Native pagination is hidden only once JS is active (graceful degradation:
 *    no JS / unsupported browser / fetch error => native pagination stays).
 *  - Re-initialises when Dawn re-renders the grid after a filter/sort change.
 *
 * Does NOT modify snippets/pagination.liquid — pagination is hidden via JS only.
 */
(function () {
  'use strict';

  var SEL = {
    container: '#ProductGridContainer',
    grid: '#product-grid',
    pagination: '.pagination',
    current: '.pagination__item--current',
    item: '.grid__item'
  };

  var SUPPORTED =
    'IntersectionObserver' in window &&
    'fetch' in window &&
    'DOMParser' in window &&
    'URL' in window;

  function InfiniteScroll(container) {
    this.container = container;
    this.grid = container.querySelector(SEL.grid);
    this.pagination = container.querySelector(SEL.pagination);
    this.sectionId = this.grid ? this.grid.dataset.id : null;

    this.baseUrl = new URL(window.location.href);
    this.currentPage = this.getCurrentPage();
    this.maxPage = this.getMaxPage(this.currentPage);
    // First page's item count = the page size (used to detect the last page).
    this.pageSize = this.grid ? this.grid.querySelectorAll(SEL.item).length : 0;

    this.isLoading = false;
    this.destroyed = false;
    this.sentinel = null;
    this.spinner = null;
    this.observer = null;
    this.onScroll = null;

    if (!this.grid || !this.sectionId || !this.pagination) return;
    if (this.currentPage >= this.maxPage) return;

    this.init();
  }

  InfiniteScroll.prototype.getCurrentPage = function () {
    var el = this.container.querySelector(SEL.current);
    var fromEl = el ? parseInt(el.textContent.trim(), 10) : NaN;
    if (!isNaN(fromEl)) return fromEl;
    var fromUrl = parseInt(this.baseUrl.searchParams.get('page'), 10);
    return isNaN(fromUrl) ? 1 : fromUrl;
  };

  InfiniteScroll.prototype.getMaxPage = function (currentPage) {
    var max = currentPage;
    var links = this.container.querySelectorAll(SEL.pagination + ' a[href]');
    links.forEach(function (a) {
      var p = parseInt(new URL(a.href, window.location.origin).searchParams.get('page'), 10);
      if (!isNaN(p) && p > max) max = p;
    });
    return max;
  };

  InfiniteScroll.prototype.init = function () {
    this.pagination.setAttribute('hidden', '');

    this.sentinel = document.createElement('div');
    this.sentinel.className = 'infinite-scroll__sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');

    this.spinner = document.createElement('div');
    this.spinner.className = 'infinite-scroll__spinner';
    this.spinner.hidden = true;
    this.spinner.innerHTML =
      '<svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>';

    // Append after the grid, inside the container (block flow, observable box).
    this.container.appendChild(this.sentinel);
    this.container.appendChild(this.spinner);

    var self = this;

    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) self.loadNext();
        });
      },
      { rootMargin: '800px 0px' }
    );
    this.observer.observe(this.sentinel);

    var ticking = false;
    this.onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        if (!self.sentinel) return;
        var rect = self.sentinel.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 800) self.loadNext();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.onScroll(); // initial check
  };

  InfiniteScroll.prototype.loadNext = function () {
    if (this.destroyed || this.isLoading) return;

    var nextPage = this.currentPage + 1;
    if (nextPage > this.maxPage) {
      this.destroy();
      return;
    }

    this.isLoading = true;
    if (this.spinner) this.spinner.hidden = false;

    var self = this;
    var url = new URL(this.baseUrl.toString());
    url.searchParams.set('page', nextPage);
    url.searchParams.set('section_id', this.sectionId);

    fetch(url.toString())
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newItems = doc.querySelectorAll(SEL.grid + ' ' + SEL.item);

        if (!newItems.length) {
          self.destroy();
          return;
        }

        var fragment = document.createDocumentFragment();
        Array.prototype.forEach.call(newItems, function (item) {
          fragment.appendChild(document.importNode(item, true));
        });
        self.grid.appendChild(fragment);

        self.currentPage = nextPage;

        // var current = new URL(window.location.href);
        // current.searchParams.set('page', nextPage);
        // window.history.replaceState({}, '', current.toString());

        // Stop if this was the last page: either we hit the known max page, or
        // this page returned fewer items than a full page (the final, partial page).
        var isLastPage =
          self.currentPage >= self.maxPage ||
          (self.pageSize > 0 && newItems.length < self.pageSize);

        if (isLastPage) {
          self.destroy();
        } else if (self.onScroll) {
          self.onScroll(); // keep filling if the viewport still has room
        }
      })
      .catch(function (err) {
        if (window.console) console.error('[infinite-scroll]', err);
        if (self.pagination) self.pagination.removeAttribute('hidden');
        self.destroy();
      })
      .then(function () {
        self.isLoading = false;
        if (self.spinner) self.spinner.hidden = true;
      });
  };

  InfiniteScroll.prototype.destroy = function () {
    this.destroyed = true;
    this.isLoading = false;
    if (this.spinner) this.spinner.hidden = true;
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      this.onScroll = null;
    }
  };

  // --------------------------------------------------------------------------
  // Boot + re-init on filter/sort re-render
  // --------------------------------------------------------------------------
  var instance = null;

  function boot() {
    if (!SUPPORTED) return;
    var container = document.querySelector(SEL.container);
    if (container) instance = new InfiniteScroll(container);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (SUPPORTED) {
    var watch = document.querySelector(SEL.container);
    if (watch) {
      var mo = new MutationObserver(function () {
        if (!watch.querySelector('.infinite-scroll__sentinel')) {
          if (instance) instance.destroy();
          instance = new InfiniteScroll(watch);
        }
      });
      mo.observe(watch, { childList: true });
    }
  }
})();